import React, { useState, useEffect } from 'react';
import CampaignEffectivenessChart from '../components/analytics/CampaignEffectivenessChart';
import ResponseRateChart from '../components/analytics/ResponseRateChart';
import GiftAnalyticsChart from '../components/analytics/GiftAnalyticsChart';
import AlumniEngagementChart from '../components/analytics/AlumniEngagementChart';
import { getCampaigns, getCampaignContacts, getGifts } from '../utils/apiService';

const Analytics = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [campaignContacts, setCampaignContacts] = useState({});
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalAlumni: 0,
    totalCampaigns: 0,
    activeCampaigns: 0,
    totalContacts: 0,
    pendingContacts: 0,
    contactedContacts: 0,
    respondedContacts: 0,
    convertedContacts: 0,
    responseRate: 0,
    conversionRate: 0,
    giftsSent: 0
  });
  const [error, setError] = useState(null);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Load campaigns
        const campaignsData = await getCampaigns();
        setCampaigns(campaignsData);
        
        // Load contacts for each campaign
        const contactsPromises = campaignsData.map(campaign => getCampaignContacts(campaign._id));
        const contactsResults = await Promise.all(contactsPromises);
        
        // Create a map of campaign ID to contacts
        const contactsMap = {};
        campaignsData.forEach((campaign, index) => {
          contactsMap[campaign._id] = contactsResults[index];
        });
        setCampaignContacts(contactsMap);
        
        // Load gifts
        const giftsData = await getGifts();
        setGifts(giftsData);
        
        // Calculate stats
        calculateStats(campaignsData, contactsMap, giftsData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  // Calculate overall statistics
  const calculateStats = (campaignsData, contactsMap, giftsData) => {
    // Count active campaigns
    const activeCampaigns = campaignsData.filter(c => c.status === 'active').length;
    
    // Initialize counters
    let totalContacts = 0;
    let pendingContacts = 0;
    let contactedContacts = 0;
    let respondedContacts = 0;
    let convertedContacts = 0;
    
    // Gift campaign specific stats
    const giftCampaigns = campaignsData.filter(c => c.sendosoEnabled);
    const giftCampaignStats = {
      totalGifts: 0,
      byType: {},
      responseRate: 0,
      conversionRate: 0
    };

    // Count contacts by status
    Object.values(contactsMap).forEach(contacts => {
      totalContacts += contacts.length;
      contacts.forEach(contact => {
        switch (contact.status) {
          case 'pending':
            pendingContacts++;
            break;
          case 'contacted':
            contactedContacts++;
            break;
          case 'responded':
            respondedContacts++;
            break;
          case 'converted':
            convertedContacts++;
            break;
        }
      });
    });
    
    // Calculate gift campaign stats
    giftCampaigns.forEach(campaign => {
      const campaignContacts = contactsMap[campaign._id] || [];
      const campaignGifts = giftsData.filter(g => g.campaignId === campaign._id);
      
      // Count gifts by type
      campaignGifts.forEach(gift => {
        giftCampaignStats.totalGifts++;
        giftCampaignStats.byType[gift.giftType] = (giftCampaignStats.byType[gift.giftType] || 0) + 1;
      });

      // Calculate response and conversion rates for gift campaigns
      const giftResponded = campaignContacts.filter(c => c.status === 'responded').length;
      const giftConverted = campaignContacts.filter(c => c.status === 'converted').length;
      
      if (campaignContacts.length > 0) {
        giftCampaignStats.responseRate = (giftResponded / campaignContacts.length) * 100;
        giftCampaignStats.conversionRate = (giftConverted / campaignContacts.length) * 100;
      }
    });
    
    // Calculate overall rates
    const responseRate = totalContacts > 0 ? (respondedContacts / totalContacts) * 100 : 0;
    const conversionRate = totalContacts > 0 ? (convertedContacts / totalContacts) * 100 : 0;
    
    setStats({
      totalAlumni: Object.values(contactsMap).flat().length,
      totalCampaigns: campaignsData.length,
      activeCampaigns,
      totalContacts,
      pendingContacts,
      contactedContacts,
      respondedContacts,
      convertedContacts,
      responseRate: parseFloat(responseRate.toFixed(1)),
      conversionRate: parseFloat(conversionRate.toFixed(1)),
      giftsSent: giftsData.length,
      giftCampaigns: giftCampaigns.length,
      giftCampaignStats: {
        ...giftCampaignStats,
        responseRate: parseFloat(giftCampaignStats.responseRate.toFixed(1)),
        conversionRate: parseFloat(giftCampaignStats.conversionRate.toFixed(1))
      }
    });
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <p className="text-lg text-gray-600">Loading analytics data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-lg text-red-600">Error loading analytics: {error}</p>
      </div>
    );
  }

  return (
    <div className="analytics-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Analytics Dashboard</h1>
        <p className="text-gray-600">Track alumni engagement and campaign performance metrics</p>
      </div>
      
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Total Campaigns</p>
          <p className="text-2xl font-bold text-gray-800">{stats.totalCampaigns}</p>
          <p className="text-sm text-gray-600">{stats.activeCampaigns} active</p>
          {stats.giftCampaigns > 0 && (
            <p className="text-sm text-gray-600">{stats.giftCampaigns} gift or anniv. campaigns</p>
          )}
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Total Contacts</p>
          <p className="text-2xl font-bold text-gray-800">{stats.totalContacts}</p>
          <div className="text-sm text-gray-600">
            <p>Pending: {stats.pendingContacts}</p>
            <p>Contacted: {stats.contactedContacts}</p>
            <p>Responded: {stats.respondedContacts}</p>
            <p>Converted: {stats.convertedContacts}</p>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Response Rate</p>
          <p className="text-2xl font-bold text-gray-800">{stats.responseRate}%</p>
          <p className="text-sm text-gray-600">Conversion: {stats.conversionRate}%</p>
          {stats.giftCampaigns > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <p className="text-sm text-gray-500">Gift Campaigns</p>
              <p className="text-sm text-gray-600">Response: {stats.giftCampaignStats.responseRate}%</p>
              <p className="text-sm text-gray-600">Conversion: {stats.giftCampaignStats.conversionRate}%</p>
            </div>
          )}
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Gifts Sent</p>
          <p className="text-2xl font-bold text-gray-800">{stats.giftsSent}</p>
          {stats.giftCampaigns > 0 && Object.entries(stats.giftCampaignStats.byType).map(([type, count]) => (
            <p key={type} className="text-sm text-gray-600 capitalize">
              {type.replace('_', ' ')}: {count}
            </p>
          ))}
        </div>
      </div>
      
      {/* Campaign Effectiveness */}
      <div className="mb-8">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Campaign Effectiveness by Type</h2>
            <p className="text-sm text-gray-600">Compare response and conversion rates across different campaign types</p>
          </div>
          <div className="p-4">
            <CampaignEffectivenessChart campaigns={campaigns} contacts={campaignContacts} />
          </div>
        </div>
      </div>
      
      {/* Response Rate Over Time */}
      <div className="mb-8">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Response Rate Trends</h2>
            <p className="text-sm text-gray-600">Track response rates over time to identify trends and patterns</p>
          </div>
          <div className="p-4">
            <ResponseRateChart campaigns={campaigns} contacts={campaignContacts} />
          </div>
        </div>
      </div>
      
      {/* Two Column Layout for Smaller Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Alumni Engagement */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Alumni Engagement Metrics</h2>
            <p className="text-sm text-gray-600">Visualize key engagement metrics across your alumni network</p>
          </div>
          <div className="p-4">
            <AlumniEngagementChart contacts={Object.values(campaignContacts).flat()} />
          </div>
        </div>
        
        {/* Gift Analytics */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Gift Analytics</h2>
            <p className="text-sm text-gray-600">Analyze the effectiveness of physical gift campaigns</p>
          </div>
          <div className="p-4">
            <GiftAnalyticsChart gifts={gifts} />
          </div>
        </div>
      </div>
      
      {/* Data Disclaimer */}
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mb-8">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">About the Analytics</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                These analytics are based on campaign and alumni data stored in your application.
                For more accurate insights, ensure your campaign statuses and contact information are kept up to date.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
